import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { IntroductionComponent } from '../components/introduction/introduction.component';
import { PopupMessageComponent } from '../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../components/spinner/spinner.component';
import { EopAssistantComponent } from '../components/eop-assistant/eop-assistant.component';
import { PrvNextButtonsComponent } from '../components/prv-next-buttons/prv-next-buttons.component';
import { Icons } from '../../shared/helpers/icons';
import { IUserInfo } from '../../shared/models/contact';
import { IEopDocument } from '../../shared/models/eopDocument';
import { ActivatedRoute, Router } from '@angular/router';
import { UserDataService } from '../../shared/services/userData.service';
import { ESectionTypes } from '../../shared/models/eop_types.enum';
import { EopPlanSlug, buildPlanPageUrl, getBuildingIdFromUrl, getPlanSlugFromUrl } from '../eop-config/eop-page-config';

interface ITrainingLink {
  label: string;
  url: string;
}

interface ITrainingResources {
  contactInfo: string;
  hotlineNote: string;
  linksAndResources?: ITrainingLink;
}

interface ITrainingDoc {
  name: string;
  recommendedCompletion: string;
  alsdePolicy?: ITrainingLink;
  alStateLaw?: ITrainingLink;
  webLinks: ITrainingLink[];
  resources?: ITrainingResources;
}

@Component({
  selector: 'mandatory-training-resources',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    IntroductionComponent,
    PopupMessageComponent,
    SpinnerComponent,
    EopAssistantComponent,
    PrvNextButtonsComponent
  ],
  templateUrl: './mandatory-training-resources.component.html',
  styleUrl: './mandatory-training-resources.component.css'
})
export class MandatoryTrainingResourcesComponent {
  
  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  currentPageRoute: string | null = null;
  currentPlan: EopPlanSlug = 'al';
  currentBuildingIdFromRoute: string | null = null;
  eopDoc: IEopDocument | null = null;
  eopSectionTypeId = ESectionTypes["Mandatory Training Resources"];
  eopSubSectionTypeId: number | null = null;
  
  trainingDocs: ITrainingDoc[] = [
    {
      name: 'State of Alabama Mandated Reporter Trainings',
      recommendedCompletion: 'October 1st',
      webLinks: [
        { label: 'State of Alabama Mandated Reporter Trainings', url: 'https://aldhr.remote-learner.net/course/index.php?categoryid=1' }
      ]
    },
    {
      name: "Erin's Law",
      recommendedCompletion: 'October 1st',
      alsdePolicy: {
        label: "Erin's Law State Guidelines for Sexual Abuse Prevention Instructional Program",
        url: "https://v2.nside.io/EOP/Training-Tabs-Uploads/Erin's%20Law%20State%20Guidelines%20for%20Sexual%20Abuse%20Prevention%20Instructional%20Program.pdf"
      },
      webLinks: [
        { label: "Erin's Law Questions and Answers", url: "https://v2.nside.io/EOP/Training-Tabs-Uploads/Erin's%20Law%20Questions%20and%20Answers.pdf" }
      ],
      resources: {
        contactInfo: "Alabama Department of Child Abuse and Neglect Prevention\nChildren's Trust Fund of Alabama\n60 Commerce Street, Suite 1000\nMontgomery, Alabama 36104\n(334) 262-2951",
        hotlineNote: "CALL 1-800-4-A-CHILD (1-800-422-4453) then push 1 to talk to a hotline counselor. The Childhelp National Child Abuse Hotline is open 24 hours a day, 7 days a week. The hotline counselors work with translators who speak more than 200 languages to help callers who speak a language other than English. All calls are anonymous. (The hotline counselors don't know who you are and you don't have to tell them.)",
        linksAndResources: { label: 'Links and Resources', url: "https://v2.nside.io/EOP/Training-Tabs-Uploads/Erin's%20Law%20Resources.pdf" }
      }
    },
    {
      name: "Annalyn's Law",
      recommendedCompletion: 'October 1st',
      alsdePolicy: {
        label: 'Model Sex Offender Supervision Policy',
        url: "https://v2.nside.io/EOP/Training-Tabs-Uploads/Model%20Sex%20Offender%20Supervision%20Policy%201.6.19%20-%20Annalyn's%20Law.pdf"
      },
      alStateLaw: {
        label: "Annalyn's Law",
        url: "https://v2.nside.io/EOP/Training-Tabs-Uploads/Annalyn's%20Law.pdf"
      },
      webLinks: [],
      resources: {
        contactInfo: "Alabama Department of Child Abuse and Neglect Prevention\nChildren's Trust Fund of Alabama\n60 Commerce Street, Suite 1000\nMontgomery, Alabama 36104\n(334) 262-2951",
        hotlineNote: "CALL 1-800-4-A-CHILD (1-800-422-4453) then push 1 to talk to a hotline counselor. The Childhelp National Child Abuse Hotline is open 24 hours a day, 7 days a week. The hotline counselors work with translators who speak more than 200 languages to help callers who speak a language other than English. All calls are anonymous. (The hotline counselors don't know who you are and you don't have to tell them.)",
        linksAndResources: { label: 'Links and Resources', url: "https://v2.nside.io/EOP/Training-Tabs-Uploads/Erin's%20Law%20Resources.pdf" }
      }
    },
    {
      name: 'Suicide Prevention',
      recommendedCompletion: 'October 1st',
      alsdePolicy: {
        label: 'Suicide Prevention Model Policy',
        url: 'https://v2.nside.io/EOP/Training-Tabs-Uploads/Jason%20Flatt%20Act%20Model%20Policy.pdf'
      },
      alStateLaw: {
        label: 'Suicide Prevention',
        url: 'https://v2.nside.io/EOP/Training-Tabs-Uploads/Jason%20Flatt%20Act.pdf'
      },
      webLinks: [
        { label: 'Suicide Prevention Student Safety Plan Suicide Protocol', url: 'https://v2.nside.io/EOP/Training-Tabs-Uploads/Jason%20Flatt%20Student%20Safety%20Plan%20Suicide%20Protocol%20Unlocked.docx' },
        { label: 'School Personnel Suicide Training - Presentation', url: 'https://v2.nside.io/EOP/Training-Tabs-Uploads/School%20Personnel%20Suicide%20Training%20-%20Presentation.pdf' },
        { label: 'ASDE - More Than Sad Training', url: 'https://v2.nside.io/EOP/Training-Tabs-Uploads/ASDE%20-%20More%20Than%20Sad%20Training.audio.pdf' }
      ]
    },
    {
      name: 'Jamari T. Williams',
      recommendedCompletion: 'October 1st',
      alsdePolicy: {
        label: 'Jamari T. Williams Student Bullying Model Policy',
        url: 'https://v2.nside.io/EOP/Training-Tabs-Uploads/Jamari%20T.%20Williams%20Student%20Bullying%20Model%20Policy%20(all%20attachments).pdf'
      },
      alStateLaw: {
        label: 'Jamari Williams Law',
        url: 'https://v2.nside.io/EOP/Training-Tabs-Uploads/Jamari%20Williams%20Law.pdf'
      },
      webLinks: []
    }
  ];
  selectedDoc: ITrainingDoc = this.trainingDocs[0];
  
  loading: boolean = false;
  messageModal: string | null = null;
  popupMessage: string | null = null;
  

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userDataService: UserDataService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.currentPlan = getPlanSlugFromUrl(this.router.url);
    this.currentBuildingIdFromRoute = getBuildingIdFromUrl(this.router.url);
    this.route.paramMap.subscribe(params => {
      this.currentPageRoute = params.get('pageRoute');
    });
  }

  getCurrentRoute(): string | null {
    const url = this.router.url.replace(/\/+$/, ''); // remove trailing slash
    const segments = url.split('/');
    return segments[-1] || segments[segments.length - 1]; // get last segment
  }

  selectDoc(doc: ITrainingDoc) {
    this.selectedDoc = doc;
  }
  
  navigateToPage(route: string): void {
    const buildingId = this.currentBuildingIdFromRoute ?? this.currentBuildingId;
    if (!buildingId) {
      return;
    }

    this.router.navigateByUrl(buildPlanPageUrl(this.currentPlan, buildingId, route));
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }
  
  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

}
